<template>
  <Teleport to="body">
    <div class="app-toaster">
      <TransitionGroup name="app-toast" tag="div" class="app-toaster__list">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="app-toaster__toast"
          :class="[`app-toaster__toast--${toast.color ?? 'default'}`]"
          role="alert"
          @mouseenter="pauseTimer(toast.id)"
          @mouseleave="resumeTimer(toast.id)"
        >
          <div class="app-toaster__body">
            <p v-if="toast.title" class="app-toaster__title">
              {{ toast.title }}
            </p>
            <p v-if="toast.description" class="app-toaster__description">
              {{ toast.description }}
            </p>
          </div>
          <button
            class="app-toaster__close"
            aria-label="Dismiss"
            type="button"
            @click="remove(toast.id)"
          >
            ✕
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
  import { useAppToast } from "~/composables/useAppToast"

  const { toasts, remove } = useAppToast()

  // Per-toast timer state: id → { timerId, remaining, startedAt }
  const timers = new Map()

  const startTimer = (toast) => {
    if (!toast.timeout || toast.timeout <= 0) return
    const remaining = toast.timeout
    const startedAt = Date.now()
    const timerId = setTimeout(() => remove(toast.id), remaining)
    timers.set(toast.id, { timerId, remaining, startedAt })
  }

  const pauseTimer = (id) => {
    const entry = timers.get(id)
    if (!entry) return
    clearTimeout(entry.timerId)
    const elapsed = Date.now() - entry.startedAt
    entry.remaining = Math.max(0, entry.remaining - elapsed)
    timers.set(id, { ...entry, timerId: null })
  }

  const resumeTimer = (id) => {
    const entry = timers.get(id)
    if (!entry || entry.remaining <= 0) return
    const startedAt = Date.now()
    const timerId = setTimeout(() => remove(id), entry.remaining)
    timers.set(id, { ...entry, timerId, startedAt })
  }

  // Start timers when new toasts are added
  watch(
    () => toasts.value.length,
    (newLen, oldLen) => {
      if (newLen > (oldLen ?? 0)) {
        // New toast(s) added — start timers for any without one
        toasts.value.forEach((toast) => {
          if (!timers.has(toast.id)) startTimer(toast)
        })
      }
      // Clean up timers for removed toasts
      timers.forEach((_, id) => {
        if (!toasts.value.find((t) => t.id === id)) {
          const entry = timers.get(id)
          if (entry?.timerId) clearTimeout(entry.timerId)
          timers.delete(id)
        }
      })
    },
  )

  onUnmounted(() => {
    timers.forEach((entry) => {
      if (entry?.timerId) clearTimeout(entry.timerId)
    })
    timers.clear()
  })
</script>

<style scoped>
  .app-toaster {
    position: fixed;
    top: 1rem;
    right: 1rem;
    z-index: 99999;
    pointer-events: none;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: 360px;
    width: calc(100vw - 2rem);
  }

  .app-toaster__list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .app-toaster__toast {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.875rem 1rem;
    border-radius: 0.5rem;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: #1e1e1e;
    color: #fff;
    pointer-events: all;
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.4),
      0 2px 4px -1px rgba(0, 0, 0, 0.3);
  }

  .app-toaster__toast--success {
    border-color: rgba(34, 197, 94, 0.4);
    background: #0f2a1a;
  }

  .app-toaster__toast--error {
    border-color: rgba(239, 68, 68, 0.4);
    background: #2a0f0f;
  }

  .app-toaster__toast--warning {
    border-color: rgba(251, 191, 36, 0.4);
    background: #2a200f;
  }

  .app-toaster__toast--info {
    border-color: rgba(59, 130, 246, 0.4);
    background: #0f1a2a;
  }

  .app-toaster__body {
    flex: 1;
    min-width: 0;
  }

  .app-toaster__title {
    font-size: 0.875rem;
    font-weight: 700;
    color: #fff;
    margin: 0;
  }

  .app-toaster__description {
    font-size: 0.8125rem;
    color: rgba(255, 255, 255, 0.7);
    margin: 0.25rem 0 0;
  }

  .app-toaster__close {
    flex-shrink: 0;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.4);
    font-size: 0.75rem;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    transition: color 150ms ease;
  }

  .app-toaster__close:hover {
    color: #fff;
  }

  /* Transition animations */
  .app-toast-enter-active,
  .app-toast-leave-active {
    transition:
      opacity 200ms ease,
      transform 200ms ease;
  }

  .app-toast-enter-from {
    opacity: 0;
    transform: translateX(100%);
  }

  .app-toast-leave-to {
    opacity: 0;
    transform: translateX(100%);
  }
</style>
